// Välj plan och period (designen Web-Betalning). Knappen skickar ägaren till
// Stripes betalsida, där kortnumret skrivs in (ADR 0075 och 0117). Bara
// ägaren betalar.
import {
  planById,
  planPrice,
  plans,
  regularPlanPrice,
  vatOn,
  type BillingPeriod,
  type Plan,
  type PlanId,
} from '@leadledger/domain';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router';
import { ApiFel, data } from '../../api/client.js';
import { useBetalning } from '../../api/queries.js';
import { useAuth, useMe } from '../../auth/auth.js';
import { krMedOre } from './belopp.js';
import { FlodesHuvud } from './Flode.js';
import { stripeSida } from './stripe-sida.js';

function begransningar(plan: Plan): string[] {
  return [
    plan.users === null
      ? 'Obegränsat antal användare'
      : plan.users === 1
        ? '1 användare'
        : `2–${plan.users} användare`,
    `${plan.quotesPerMonth} offerter per månad`,
    plan.forms === 1 ? '1 formulär' : `Upp till ${plan.forms} formulär`,
    plan.senderDomain ? 'Utskick från er domän' : 'Utskick från LeadLedger',
  ];
}

function Bock() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m5 12 5 5 9-10" />
    </svg>
  );
}

/** Problemets text från API:t, eller en allmän. */
function felText(err: unknown): string {
  const detail = err instanceof ApiFel ? (err.problem as { detail?: unknown })?.detail : undefined;
  return typeof detail === 'string'
    ? detail
    : 'Det gick inte att öppna betalsidan. Försök igen om en stund.';
}

export function ValjPlan() {
  const me = useMe();
  const { api } = useAuth();
  const betalning = useBetalning();
  const [period, setPeriod] = useState<BillingPeriod>('month');
  const [planId, setPlanId] = useState<PlanId>('byra');
  const agare = me.user.role === 'owner';
  const kanBetala = betalning.data?.paymentsEnabled === true;
  const betala = useMutation({
    mutationFn: async () =>
      data(await api.POST('/v1/admin/billing/checkout', { body: { plan: planId, period } })),
    onSuccess: ({ url }) => stripeSida.oppna(url),
  });

  useEffect(() => {
    document.title = 'Välj plan – LeadLedger';
  }, []);

  const plan = planById(planId);
  const ar = period === 'year';
  const netto = planPrice(plan, period);
  const moms = vatOn(netto);
  const per = ar ? 'per år' : 'per månad';

  function skicka(e: FormEvent) {
    e.preventDefault();
    betala.mutate();
  }

  // En byrå som redan har betalat byter plan i Stripes kundportal.
  if (betalning.data?.subscription?.paid) return <Navigate to="/valj-plan/klar" replace />;

  return (
    <div className="kom-igang betalning">
      <FlodesHuvud aktivt={1} />
      <main id="innehall" className="kom-igang-sida">
        <div>
          <h1>Välj plan för {me.tenant.name}</h1>
          <p className="ingress">
            Alla planer har samma funktioner för offerter. Ingen bindningstid, och ni byter plan när
            ni vill. <Link to="/testlage">Prova i testläget först</Link> och välj plan senare.
          </p>
        </div>
        {!agare && (
          <div className="kort notis" role="note">
            Bara byråns ägare kan välja plan och betala. Be ägaren att logga in och välja.
          </div>
        )}
        {betalning.isSuccess && !kanBetala && (
          <div className="kort notis" role="note">
            Kortbetalningen är inte öppen än. Kontakta oss på{' '}
            <a href="mailto:info@leadledger.se">info@leadledger.se</a>, så kommer ni igång.
          </div>
        )}
        <form className="betalning-kolumner" onSubmit={skicka}>
          <div className="betalning-val">
            <fieldset className="period-val" disabled={!agare}>
              <legend>Betalningsperiod</legend>
              <div className="segment">
                <label className={ar ? undefined : 'vald'}>
                  <input
                    type="radio"
                    name="period"
                    checked={!ar}
                    onChange={() => setPeriod('month')}
                  />
                  Månadsvis
                </label>
                <label className={ar ? 'vald' : undefined}>
                  <input
                    type="radio"
                    name="period"
                    checked={ar}
                    onChange={() => setPeriod('year')}
                  />
                  Årsvis <span className="chip chip-gron">2 mån gratis</span>
                </label>
              </div>
            </fieldset>
            <fieldset disabled={!agare}>
              <legend>Plan</legend>
              <div className="planer">
                {plans.map((p) => (
                  <label key={p.id} className={`plan-val${p.id === planId ? ' vald' : ''}`}>
                    <input
                      type="radio"
                      name="plan"
                      checked={p.id === planId}
                      onChange={() => setPlanId(p.id)}
                    />
                    <span>
                      <span className="plan-namn">
                        {p.name}
                        {p.popular && <span className="chip chip-gron">Vanligast</span>}
                      </span>
                      <span className="plan-for">{p.description}</span>
                      <span className="plan-granser">
                        {begransningar(p).map((g) => (
                          <span key={g}>{g}</span>
                        ))}
                      </span>
                    </span>
                    <span className="plan-pris">
                      <span className="mono">{krMedOre(planPrice(p, period))}</span>
                      <span>{ar ? 'per år exkl. moms' : 'per månad exkl. moms'}</span>
                      {/* Rabatterat pris, med ordinarie pris överstruket (ADR 0118). */}
                      <span className="ordinarie">
                        Ordinarie <s>{krMedOre(regularPlanPrice(p, period))}</s>
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              {/* Större byråer får pris på begäran (ADR 0116). LeadLedgers enda adress (ADR 0086). */}
              <p className="dampad">
                Fler än 5 användare? Kontakta oss på{' '}
                <a href="mailto:info@leadledger.se">info@leadledger.se</a>, så får ni ett pris.
              </p>
            </fieldset>
            {/* Organisationsnumret sparas vid registreringen i steg 10.6 (ADR 0075). */}
            <div className="kort faktura">
              <div>
                <div className="dampad">Fakturan ställs till</div>
                <div className="fet">{me.tenant.name}</div>
                <div className="text-3">{me.user.email}</div>
              </div>
              <span className="dampad">
                Adress och eventuell annan fakturamejl fyller ni i hos Stripe.
              </span>
            </div>
          </div>
          <aside className="kort sammanfattning" aria-labelledby="sammanfattning-rubrik">
            <h2 id="sammanfattning-rubrik">Sammanfattning</h2>
            <div className="rad forsta">
              <span>
                {plan.name}, {per}
              </span>
              <span className="mono">{krMedOre(netto)}</span>
            </div>
            <div className="rad">
              <span>Moms 25 %</span>
              <span className="mono">{krMedOre(moms)}</span>
            </div>
            <div className="rad summa">
              <span>Att betala {per}</span>
              <span className="mono">{krMedOre(netto + moms)}</span>
            </div>
            <p className="not">
              {ar
                ? `Motsvarar ${krMedOre(Math.round(netto / 12 / 100) * 100)} i månaden. Förnyas efter ett år. Ni kan säga upp när som helst, och planen gäller året ut.`
                : 'Förnyas varje månad. Ni kan säga upp när som helst, och planen gäller månaden ut.'}
            </p>
            {betala.isError && (
              <p className="meddelande" role="alert">
                {felText(betala.error)}
              </p>
            )}
            <button
              type="submit"
              className="btn btn-p"
              disabled={!agare || !kanBetala || betala.isPending || betala.isSuccess}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="5" y="11" width="14" height="10" rx="2" />
                <path d="M8 11V8a4 4 0 0 1 8 0v3" />
              </svg>
              {betala.isPending || betala.isSuccess
                ? 'Öppnar Stripes betalsida …'
                : 'Betala med kort hos Stripe'}
            </button>
            <p className="not dampad">
              Ni skickas till Stripes säkra betalsida. Kortnumret skrivs bara in där, aldrig hos
              oss.
            </p>
            <ul className="lofte">
              <li>
                <Bock />
                Ingen bindningstid, säg upp i kontot
              </li>
              <li>
                <Bock />
                Inga extra avgifter om offerterna tar slut
              </li>
              <li>
                <Bock />
                Kvitto och faktura med moms till er e-post
              </li>
            </ul>
          </aside>
        </form>
      </main>
    </div>
  );
}
